import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { RtComponentOutlet } from '@core/common-legacy';
import { injectTellentInterruptRenderers } from '@core/tellent-assistant-contracts';
import { ChatPart } from '@core/tellent-assistant-types';

import { TellentAssistantPartFallbackComponent } from '../fallback/tellent-assistant-part-fallback.component';

@Component({
  selector: 'tellent-assistant-interrupt-part',
  standalone: true,
  imports: [RtComponentOutlet, TellentAssistantPartFallbackComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (rendererCmp(); as cmp) {
      <ng-container
        [rtComponentOutlet]="cmp"
        [rtComponentOutletInputs]="rendererInputs()"
        [rtComponentOutletOutputs]="rendererOutputs"
      />
    } @else {
      <tellent-assistant-part-fallback [reason]="part().reason" [payload]="part().payload" />
    }
  `,
})
export class TellentAssistantInterruptPartComponent {
  public readonly part = input.required<Extract<ChatPart, { kind: 'interrupt' }>>();
  public readonly resolve = output<unknown>();
  public readonly cancel = output<void>();

  private readonly registry = injectTellentInterruptRenderers({ optional: true }) ?? [];
  private readonly match = computed(() => this.registry.find(r => r.reason === this.part().reason));

  protected readonly rendererCmp = computed(() => {
    const m = this.match();
    if (!m) return null;
    return m.schema.safeParse(this.part().payload).success ? m.component : null;
  });

  protected readonly rendererInputs = computed<Record<string, unknown> | undefined>(() => {
    const m = this.match();
    if (!m) return undefined;
    const res = m.schema.safeParse(this.part().payload);
    if (!res.success) return undefined;
    return { payload: res.data, resolved: this.part().resolved };
  });

  protected readonly rendererOutputs = {
    resolve: (payload: unknown) => this.resolve.emit(payload),
    cancel: () => this.cancel.emit(),
  };
}
